import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast, Toaster } from "sonner";
import { CalendarClock, LogOut, ArrowRight } from "lucide-react";
import { SavedVehicles } from "@/components/SavedVehicles";
import { BookingRow } from "@/components/BookingRow";
import { assignBookings } from "@/lib/vehicleMatch";

export default function CustomerAccountPage() {
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(true);
  const nav = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    const run = async () => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const hash = location.hash || "";
      if (hash.includes("session_id=")) {
        if (processed.current) return;
        processed.current = true;
        const sessionId = new URLSearchParams(hash.slice(1)).get("session_id");
        window.history.replaceState(null, "", window.location.pathname);
        try {
          const r = await api.post("/customer/google", { session_id: sessionId });
          setUser(r.data);
        } catch {
          toast.error("Google sign-in failed — please try again.");
          nav("/account/login");
          return;
        } finally {
          setChecking(false);
        }
        return;
      }
      try {
        const r = await api.get("/customer/me");
        setUser(r.data);
      } catch {
        nav("/account/login");
      } finally {
        setChecking(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/customer/bookings").then((r) => setData(r.data)).catch(() => {});
    loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [vehicles, setVehicles] = useState([]);
  const loadVehicles = () => api.get("/customer/vehicles").then((r) => setVehicles(r.data)).catch(() => {});

  const { byVehicle, unmatched } = useMemo(
    () => assignBookings(data?.bookings || [], vehicles),
    [data, vehicles]
  );

  const logout = async () => {
    await api.post("/customer/logout").catch(() => {});
    nav("/");
  };

  if (checking || !user) {
    return <div className="bg-background min-h-screen flex items-center justify-center text-zinc-500 font-mono text-sm uppercase tracking-widest">Loading your garage…</div>;
  }

  const next = data?.next_service;
  const dueSoon = next && new Date(next.due_date) - new Date() < 21 * 24 * 3600 * 1000;

  return (
    <div className="bg-background min-h-screen pt-28 pb-20 hud-grid-bg" data-testid="customer-account-page">
      <Toaster theme="dark" position="top-center" />
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-red blink-dot" />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-red">My garage · Telemetry online</p>
            </div>
            <h1 className="mt-2 font-display uppercase text-3xl sm:text-4xl tracking-tight text-white">G'day, {(user.name || "").split(" ")[0]}</h1>
          </div>
          <button data-testid="customer-logout" onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-300 text-xs font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red">
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </div>

        <div className="mt-8 hud-panel rounded-xl p-6 flex flex-wrap items-center gap-5" data-testid="next-service-card">
          <span className={`h-12 w-12 rounded flex items-center justify-center ${next ? "bg-brand-red shadow-[0_0_18px_rgba(225,29,43,0.5)]" : "bg-zinc-800"}`}>
            <CalendarClock className="h-6 w-6 text-white" />
          </span>
          <div className="flex-1 min-w-[220px]">
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Next service due</p>
            {next ? (
              <>
                <p className="text-xl font-display uppercase text-white">{next.due_date}{dueSoon && <span className="ml-3 text-sm text-brand-red">Due soon</span>}</p>
                <p className="text-xs text-zinc-500">{next.vehicle} · last serviced {(next.last_service || "").slice(0, 10)}</p>
              </>
            ) : (
              <p className="text-sm text-zinc-400">No completed services yet — once we finish a job, your next due date appears here and we'll remind you by SMS + email two weeks out.</p>
            )}
          </div>
          <Link data-testid="account-book-btn" to="/book" className="glow-btn rounded inline-flex items-center gap-2 px-6 py-3 bg-brand-red text-white font-display uppercase tracking-tight hover:bg-[#B91C2B]">
            Book a service <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-12">
          <SavedVehicles vehicles={vehicles} bookings={data?.bookings || []} bookingsByVehicle={byVehicle} onChanged={loadVehicles} userEmail={user.email} />
        </div>

        <h2 className="mt-12 font-display uppercase text-2xl tracking-tight text-white">
          {vehicles.length > 0 ? "Other bookings" : "Your bookings & service history"}
        </h2>
        {vehicles.length > 0 && (
          <p className="mt-1 text-xs text-zinc-500">Bookings for your saved vehicles live in each vehicle's logbook above.</p>
        )}
        <div className="mt-5 space-y-px bg-zinc-900 border border-zinc-900" data-testid="customer-bookings-list">
          {(data?.bookings || []).length === 0 && (
            <div className="bg-zinc-950 p-8 text-center text-sm text-zinc-500">
              Nothing here yet — bookings made with <span className="text-zinc-300">{user.email}</span> appear automatically.
            </div>
          )}
          {(data?.bookings || []).length > 0 && unmatched.length === 0 && (
            <div className="bg-zinc-950 p-6 text-center text-xs text-zinc-500">
              All your bookings are filed under your saved vehicles above.
            </div>
          )}
          {unmatched.map((b) => <BookingRow key={b.id} b={b} />)}
        </div>
      </div>
    </div>
  );
}
